import type { AudioElement } from 'utils/webrtc';

/**
 * Complete state snapshot of a media stream across three layers
 */
export interface MediaStreamDiagnostics {
  // Identifiers
  feedId: string;
  mid: string;
  publisherMid: string;
  kind: string;

  // StreamMedia state (our application layer)
  hasTrack: boolean;
  subscriptionState: string;
  receivingState: string;
  // WebKit gates an element's first media load on page in-window state; visibility at
  // sample time is the discriminator (see docs/monitoring/audio-loss/2026-08-11-…).
  hidden: boolean;

  // MediaStreamTrack state (browser WebRTC layer)
  trackId?: string;
  trackReadyState?: string; // 'live' or 'ended' - MOST IMPORTANT!
  trackEnabled?: boolean;
  trackMuted?: boolean;

  // MediaStream state (browser WebRTC container)
  mediaStreamId?: string;
  mediaStreamActive?: boolean;

  // HTMLMediaElement state (DOM audio/video playback)
  hasMediaElement: boolean;
  mediaElementId?: string;
  mediaElementPaused?: boolean;
  mediaElementMuted?: boolean;
  mediaElementVolume?: number;
  mediaElementCurrentTime?: number; // Should increment - CRITICAL for detecting flow
  mediaElementReadyState?: number; // 0-4, HAVE_NOTHING to HAVE_ENOUGH_DATA
  mediaElementError?: string;

  // HTMLVideoElement-specific state (proof of frame delivery)
  videoWidth?: number;
  videoHeight?: number;
}

/**
 * Extracts media stream state for diagnostic logging
 *
 * This utility function consolidates stream state extraction to ensure consistency
 * across all diagnostic and health check logging throughout the application.
 *
 * It captures state from three layers:
 * 1. Application state (StreamMedia) - our state management
 * 2. WebRTC state (MediaStreamTrack/MediaStream) - browser's real-time communication
 * 3. DOM state (HTMLMediaElement) - media playback element
 */
export function extractMediaStreamState(
  feedId: string,
  mid: string,
  stream: any, // StreamMedia type - using any to avoid circular dependencies
  mediaElement?: AudioElement | HTMLVideoElement | null
): MediaStreamDiagnostics {
  let track = null;
  try {
    track = stream?.getTrack ? stream.getTrack() : null;
  } catch {
    // Track may have been disposed
  }
  const { mediaStream } = stream || {};

  const result: Partial<MediaStreamDiagnostics> = {
    // Always include: identifiers (full UUIDs as required)
    feedId: feedId || 'unknown',
    mid: mid || '',
    publisherMid: stream?.publisherMid || '',
    kind: stream?.kind || 'unknown',

    // Always include: critical application state
    hasTrack: stream?.hasTrack || false,
    subscriptionState: String(stream?.subscriptionState || 'unknown'),

    // Always include: critical track state (if exists)
    trackReadyState: track?.readyState,

    // Always include: media stream identifier and health indicator
    mediaStreamId: mediaStream?.id,
    mediaStreamActive: mediaStream?.active,

    // Always include: element presence and critical time
    hasMediaElement: !!mediaElement,
    mediaElementCurrentTime: mediaElement?.currentTime,
    mediaElementReadyState: mediaElement?.readyState,
    hidden: document.hidden,
  };

  // Conditionally include: trackId only if track exists and subscribed
  if (track) {
    result.trackId = track.id;

    // Only include if not in normal state (enabled=true, muted=false)
    if (track.enabled === false) {
      result.trackEnabled = false;
    }
    if (track.muted === true) {
      result.trackMuted = true;
    }
  }

  // Conditionally include: receivingState only if different from subscriptionState
  const receivingState = String(stream?.receivingState || 'unknown');
  if (receivingState !== result.subscriptionState) {
    result.receivingState = receivingState;
  }

  // Conditionally include: element state only if not in normal state
  if (mediaElement) {
    // mediaElementId kept as requested for context correlation
    if (mediaElement.id) {
      result.mediaElementId = mediaElement.id;
    }

    // Only include if paused (playing is normal)
    if (mediaElement.paused === true) {
      result.mediaElementPaused = true;
    }

    // Only include if muted (unmuted is normal)
    if (mediaElement.muted === true) {
      result.mediaElementMuted = true;
    }

    // Only include if volume is not 1.0 (full volume is normal)
    if (mediaElement.volume !== 1) {
      result.mediaElementVolume = mediaElement.volume;
    }

    // Only include error if present
    const elementError = (mediaElement as HTMLAudioElement)?.error?.message;
    if (elementError) {
      result.mediaElementError = elementError;
    }

    // Video dimensions — proof that frames are being decoded
    if (mediaElement instanceof HTMLVideoElement) {
      result.videoWidth = mediaElement.videoWidth;
      result.videoHeight = mediaElement.videoHeight;
    }
  }

  return result as MediaStreamDiagnostics;
}

/**
 * Determines if a stream is in a problematic state that needs attention
 *
 * Used by hybrid logging to identify streams that should be logged with full details
 * even when they weren't the stream that triggered the log event.
 */
export function isStreamProblematic(diagnostics: MediaStreamDiagnostics): boolean {
  // Track is dead but we think we're subscribed
  if (diagnostics.trackReadyState === 'ended' && diagnostics.subscriptionState === '2') {
    return true;
  }

  // Has errors
  if (diagnostics.mediaElementError) {
    return true;
  }

  // Track exists but is disabled or muted unexpectedly
  if (diagnostics.trackEnabled === false || diagnostics.trackMuted === true) {
    return true;
  }

  // Media element is paused or muted unexpectedly (audio only — video elements may be
  // briefly paused during init, and muted is irrelevant since audio uses separate elements)
  if (diagnostics.kind === 'audio') {
    if (diagnostics.mediaElementPaused === true || diagnostics.mediaElementMuted === true) {
      return true;
    }
  }

  // Media stream not active
  if (diagnostics.mediaStreamActive === false) {
    return true;
  }

  return false;
}
